import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Building2, Users, UserCircle, ClipboardList, Crown, Rocket, CheckCircle, XCircle, ToggleLeft, ToggleRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = { free: 'フリー', standard: 'スタンダード', pro: 'プロ' };
const PLAN_COLORS = {
  free: 'bg-gray-100 text-gray-700 border-gray-200',
  standard: 'bg-primary/10 text-primary border-primary/20',
  pro: 'bg-amber-100 text-amber-800 border-amber-200'
};

export default function AdminDashboardPage() {
  const [facilities, setFacilities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [facRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/facilities`),
        axios.get(`${API}/admin/stats`)
      ]);
      setFacilities(facRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleToggle = async (facilityId) => {
    try {
      await axios.put(`${API}/admin/facilities/${facilityId}/toggle`);
      fetchData();
    } catch (e) { alert('変更に失敗: ' + (e.response?.data?.detail || e.message)); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">システム管理</h1>
        <p className="text-muted-foreground mt-1">全事業所の管理・利用状況</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="card-surface text-center">
            <CardContent className="pt-4 pb-3">
              <Building2 size={24} className="mx-auto mb-1 text-primary" />
              <p className="text-2xl font-bold">{stats.total_facilities}</p>
              <p className="text-xs text-muted-foreground">事業所</p>
            </CardContent>
          </Card>
          <Card className="card-surface text-center">
            <CardContent className="pt-4 pb-3">
              <UserCircle size={24} className="mx-auto mb-1 text-violet-500" />
              <p className="text-2xl font-bold">{stats.total_users}</p>
              <p className="text-xs text-muted-foreground">ユーザー</p>
            </CardContent>
          </Card>
          <Card className="card-surface text-center">
            <CardContent className="pt-4 pb-3">
              <Users size={24} className="mx-auto mb-1 text-emerald-500" />
              <p className="text-2xl font-bold">{stats.total_clients}</p>
              <p className="text-xs text-muted-foreground">利用者</p>
            </CardContent>
          </Card>
          <Card className="card-surface text-center">
            <CardContent className="pt-4 pb-3">
              <ClipboardList size={24} className="mx-auto mb-1 text-amber-500" />
              <p className="text-2xl font-bold">{stats.total_records}</p>
              <p className="text-xs text-muted-foreground">支援記録</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plan distribution */}
      {stats?.plan_distribution && (
        <Card className="card-surface">
          <CardHeader><CardTitle>プラン分布</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-6">
              {Object.entries(PLAN_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${PLAN_COLORS[key]}`}>{label}</span>
                  <span className="font-bold">{stats.plan_distribution[key] || 0}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Facilities list */}
      <Card className="card-surface">
        <CardHeader><CardTitle>事業所一覧 ({facilities.length}件)</CardTitle></CardHeader>
        <CardContent>
          {facilities.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">事業所がありません</p>
          ) : (
            <div className="space-y-3">
              {facilities.map(f => (
                <div key={f.id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors" data-testid={`admin-facility-${f.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${f.is_active ? 'bg-primary/10' : 'bg-gray-200'}`}>
                      <Building2 size={20} className={f.is_active ? 'text-primary' : 'text-gray-400'} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{f.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${PLAN_COLORS[f.plan_id]}`}>
                          {PLAN_LABELS[f.plan_id] || f.plan_id}
                        </span>
                        {!f.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">無効</span>}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground mt-0.5">
                        <span>{f.municipality}</span>
                        <span>ユーザー: {f.user_count}</span>
                        <span>利用者: {f.client_count}</span>
                        {f.created_at && <span>登録: {f.created_at.split('T')[0]}</span>}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleToggle(f.id)}
                    className={f.is_active ? '' : 'text-emerald-600 hover:text-emerald-600'}
                    data-testid={`toggle-facility-${f.id}`}
                  >
                    {f.is_active ? <><ToggleRight size={16} className="mr-1" />有効</> : <><ToggleLeft size={16} className="mr-1" />無効</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
